import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getSocket, initializeSocket } from "./chatServices";
import { useNavigate, useLocation } from "react-router-dom";

interface IncomingMessage {
  id: string;
  channelId: string;
  senderId: string;
  senderName: string;
  content: string;
  type?: string;
  channelName?: string | null;
  isGroup?: boolean;
}

const GlobalMessagePopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState<IncomingMessage | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isReplying, setIsReplying] = useState(false);

  const _user = useSelector((state: any) => state?.user?.user);
  const currentUserId = String(_user?.userDetails?.id ?? "");

  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest("input")) return;
    e.preventDefault();
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: rect.left, origY: rect.top,
    };
    document.body.style.cursor = "grabbing";
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
    };
    const onMouseUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Auto-hide popup after 3 minutes if not replying
  const resetHideTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!isReplying) {
        setIsVisible(false);
      }
    }, 180000); // 3 minutes
  };

  const locationRef = useRef(location.pathname);
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { currentUserIdRef.current = currentUserId; }, [currentUserId]);

  useEffect(() => {
    let socket = getSocket();
    if (!socket) {
      socket = initializeSocket();
    }
    if (!socket) return;

    if (socket.connected) {
      socket.emit("join-channels");
    }
    const onConnect = () => socket.emit("join-channels");
    socket.on("connect", onConnect);

    const handleNewMessage = (msg: any) => {
      if (locationRef.current.includes("/application/chat")) {
        return;
      }

      if (String(msg.senderId) === currentUserIdRef.current) {
        return;
      }

      if (msg.type === "call") {
        return;
      }

      setMessage({
        id: msg.id,
        channelId: String(msg.channelId),
        senderId: String(msg.senderId),
        senderName: msg.senderName || "User",
        content: msg.content || (msg.attachments?.length ? "📎 Attachment" : "New message"),
        channelName: msg.channelName || null,
        isGroup: !!msg.isGroup,
      });

      setIsVisible(true);
      setIsReplying(false);
      setReplyText("");
      resetHideTimeout();
    };

    socket.on("new-message", handleNewMessage);

    return () => {
      socket.off("new-message", handleNewMessage);
      socket.off("connect", onConnect);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  const handleGoToChat = () => {
    setIsVisible(false);
    navigate("/application/chat");
  };

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || !message) return;

    const socket = getSocket();
    if (socket) {
      socket.emit("send-message", {
        channelId: message.channelId,
        content: replyText,
        attachments: [],
        type: "text",
      });
    }

    setIsVisible(false);
    setReplyText("");
    setIsReplying(false);
  };

  if (!isVisible || !message) return null;

  const posStyle: React.CSSProperties = pos
    ? { bottom: "auto", right: "auto", left: pos.x, top: pos.y, margin: 0 }
    : {};

  return (
    <div
      ref={rootRef}
      onMouseDown={onMouseDown}
      style={{
        position: "fixed",
        bottom: 28,
        right: 28,
        zIndex: 99999,
        backgroundColor: "#12121f",
        borderRadius: 20,
        boxShadow: "0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07)",
        width: 300,
        overflow: "hidden",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        animation: pos ? "none" : "slideInUpMsg 0.25s ease-out",
        cursor: "grab",
        ...posStyle,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          cursor: "pointer",
          transition: "background 0.15s",
        }}
        onClick={handleGoToChat}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: message.isGroup
                ? "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)"
                : "linear-gradient(135deg, #6c63ff 0%, #3b3086 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            {message.isGroup ? <i className="ti ti-users" style={{ fontSize: 18 }} /> : message.senderName.charAt(0).toUpperCase()}
          </div>
          <div>
            {message.isGroup && message.channelName ? (
              <>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  background: "linear-gradient(90deg, rgba(124,58,237,0.35) 0%, rgba(79,70,229,0.25) 100%)",
                  border: "1px solid rgba(167,139,250,0.35)",
                  borderRadius: 20,
                  padding: "2px 10px 2px 6px",
                  marginBottom: 4,
                }}>
                  <i className="ti ti-users" style={{ fontSize: 11, color: "#a78bfa" }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#c4b5fd", letterSpacing: "0.1px" }}>
                    {message.channelName}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>
                  <span style={{ color: "rgba(255,255,255,0.3)" }}>from </span>
                  <span style={{ fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>{message.senderName}</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, fontSize: 15, color: "#fff", letterSpacing: "-0.2px" }}>
                  {message.senderName}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 }}>
                  New Message
                </div>
              </>
            )}
          </div>
        </div>
        <button
          onClick={handleClose}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "none",
            color: "rgba(255,255,255,0.6)",
            cursor: "pointer",
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.16)";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
            e.currentTarget.style.color = "rgba(255,255,255,0.6)";
          }}
        >
          <i className="ti ti-x" style={{ fontSize: 14 }}></i>
        </button>
      </div>

      {/* Body */}
      <div
        style={{ padding: "16px 20px", cursor: "pointer" }}
        onClick={handleGoToChat}
      >
        <div
          style={{
            fontSize: 14,
            color: "rgba(255,255,255,0.85)",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.5,
          }}
        >
          {message.content}
        </div>
      </div>

      {/* Footer / Reply Action */}
      <div style={{ padding: "14px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", backgroundColor: "rgba(0,0,0,0.15)" }}>
        {!isReplying ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsReplying(true);
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
            }}
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.08)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.1)",
              padding: "10px 14px",
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 13,
              cursor: "pointer",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)")}
          >
            Reply
          </button>
        ) : (
          <form onSubmit={handleSendReply} style={{ display: "flex", gap: 10 }}>
            <input
              autoFocus
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type a reply..."
              style={{
                flex: 1,
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(0,0,0,0.2)",
                color: "#fff",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={!replyText.trim()}
              style={{
                background: replyText.trim() ? "#6c63ff" : "rgba(255,255,255,0.1)",
                color: replyText.trim() ? "#fff" : "rgba(255,255,255,0.4)",
                border: "none",
                padding: "10px 16px",
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: replyText.trim() ? "pointer" : "default",
                transition: "background 0.2s, transform 0.1s",
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = replyText.trim() ? "scale(0.96)" : "none")}
              onMouseUp={(e) => (e.currentTarget.style.transform = "none")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "none")}
            >
              Send
            </button>
          </form>
        )}
      </div>

      <style>{`
        @keyframes slideInUpMsg {
          from {
            transform: translateY(30px) scale(0.97);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default GlobalMessagePopup;
